// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Android GNSS constellation status — the observability half of accuracy.
 *
 * Neither SBAS/EGNOS nor L5 can be requested by an app; both are chipset and
 * system-level with no public API. What this exposes is the measurement: which
 * constellations went into the fix, whether an SBAS augmentation satellite was
 * among them, and whether the device is tracking the L5 band. That is what lets
 * accuracy telemetry distinguish a 2 m device from a 5 m one instead of pooling
 * every phone into one number.
 *
 * Android only, and optional even there: `requireOptionalNativeModule` returns
 * null on web, on iOS, and in any client built before this module existed, so
 * every caller must treat a null snapshot as "unknown", never as "bad".
 */

import { requireOptionalNativeModule, type EventSubscription } from 'expo-modules-core';

export type GnssSnapshot = {
  /** Device clock in ms when the platform reported this status. */
  at: number;
  satellitesVisible: number;
  /** Satellites that actually contributed to the fix. */
  satellitesUsed: number;
  sbasUsed: number;
  /** Whether an SBAS/EGNOS augmentation satellite was used in the fix. */
  sbasInUse: boolean;
  /** Whether the OS can report carrier frequency at all (Android 8+). When
   *  false, every l5 field below is null: unknown, not absent. */
  l5Supported: boolean;
  l5Used: number | null;
  l5InUse: boolean | null;
  l5Visible: number | null;
  /** Constellations used in the fix, e.g. ['galileo', 'gps', 'sbas']. */
  constellations: string[];
  /** Mean carrier-to-noise of the used satellites in dB-Hz, or null with no fix. */
  meanCn0DbHz: number | null;
};

type GnssStatusNativeModule = {
  isAvailable: () => boolean;
  getSnapshot: () => GnssSnapshot | null;
  addListener: (
    event: 'onGnssSnapshot',
    listener: (snapshot: GnssSnapshot) => void
  ) => EventSubscription;
};

const native = requireOptionalNativeModule<GnssStatusNativeModule>('GoffyGnssStatus');

/** Whether GNSS status can be read here: an Android build carrying the module,
 *  with GNSS hardware and the location permission already granted. */
export const isGnssStatusAvailable = (): boolean => {
  try {
    return native?.isAvailable() ?? false;
  } catch {
    return false;
  }
};

/**
 * Subscribe to constellation snapshots, roughly one a second.
 *
 * The native listener is registered on the first subscriber and torn down with
 * the last, so nothing is observed unless something is watching. Returns a no-op
 * unsubscribe where the module is unavailable.
 */
export const subscribeGnssStatus = (
  listener: (snapshot: GnssSnapshot) => void
): (() => void) => {
  if (!native) return () => undefined;
  try {
    const subscription = native.addListener('onGnssSnapshot', listener);
    return () => {
      try {
        subscription.remove();
      } catch {
        // Being discarded either way.
      }
    };
  } catch {
    return () => undefined;
  }
};

/** The last snapshot the native side saw, or null when unavailable or not yet known. */
export const getGnssSnapshot = (): GnssSnapshot | null => {
  try {
    return native?.getSnapshot() ?? null;
  } catch {
    return null;
  }
};
